import {Controller} from "@hotwired/stimulus"
import {visit} from "@hotwired/turbo"

export default class extends Controller {
    static targets = ["indicator", "icon", "text"]
    static values = {
        threshold: {type: Number, default: 80},
        enabled: {type: Boolean, default: true}
    }

    connect() {
        this.startY = 0
        this.currentY = 0
        this.pullDistance = 0
        this.isRefreshing = false
        this.isPulling = false

        // Only enable on mobile devices or PWA
        if (!this.isMobileDevice() && !this.isPWA()) {
            this.enabledValue = false
            return
        }

        this.bindEvents()
        this.createIndicator()
    }

    disconnect() {
        this.unbindEvents()
    }

    bindEvents() {
        if (!this.enabledValue) return

        // Touch events for mobile
        this.element.addEventListener('touchstart', this.handleTouchStart.bind(this), {passive: false})
        this.element.addEventListener('touchmove', this.handleTouchMove.bind(this), {passive: false})
        this.element.addEventListener('touchend', this.handleTouchEnd.bind(this), {passive: false})

        // Mouse events for desktop testing
        this.element.addEventListener('mousedown', this.handleMouseDown.bind(this))
        this.element.addEventListener('mousemove', this.handleMouseMove.bind(this))
        this.element.addEventListener('mouseup', this.handleMouseUp.bind(this))
    }

    unbindEvents() {
        this.element.removeEventListener('touchstart', this.handleTouchStart)
        this.element.removeEventListener('touchmove', this.handleTouchMove)
        this.element.removeEventListener('touchend', this.handleTouchEnd)
        this.element.removeEventListener('mousedown', this.handleMouseDown)
        this.element.removeEventListener('mousemove', this.handleMouseMove)
        this.element.removeEventListener('mouseup', this.handleMouseUp)
    }

    createIndicator() {
        if (this.hasIndicatorTarget) return

        const indicator = document.createElement('div')
        indicator.className = 'pull-to-refresh-indicator'
        indicator.innerHTML = `
      <div class="pull-to-refresh-content">
        <div class="pull-to-refresh-icon" data-pull-to-refresh-target="icon">
          <svg class="animate-spin -ml-1 mr-3 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
        <span class="pull-to-refresh-text" data-pull-to-refresh-target="text">Pull to refresh</span>
      </div>
    `

        this.element.insertBefore(indicator, this.element.firstChild)
        this.indicatorTarget = indicator
    }

    handleTouchStart(event) {
        if (!this.canPull()) return

        this.startY = event.touches[0].clientY
        this.isPulling = false
    }

    handleTouchMove(event) {
        if (!this.canPull() || this.isRefreshing) return

        this.currentY = event.touches[0].clientY
        this.pullDistance = Math.max(0, this.currentY - this.startY)

        // Use the same robust scroll detection as canPull()
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0
        const isAtTop = scrollTop <= 0

        if (this.pullDistance > 10 && isAtTop) {
            event.preventDefault()
            this.isPulling = true
            this.updateIndicator()

            // Prevent iOS bounce/rubber band effect
            document.body.style.overflow = 'hidden'
        }
    }

    handleTouchEnd(event) {
        // Restore body overflow to allow normal scrolling
        document.body.style.overflow = ''

        if (!this.isPulling || this.isRefreshing) return

        if (this.pullDistance >= this.thresholdValue) {
            this.triggerRefresh()
        } else {
            this.resetIndicator()
        }

        this.isPulling = false
        this.pullDistance = 0
    }

    // Mouse events for desktop testing
    handleMouseDown(event) {
        if (!this.canPull()) return
        this.startY = event.clientY
        this.isPulling = false
    }

    handleMouseMove(event) {
        if (!this.canPull() || this.isRefreshing || !event.buttons) return

        this.currentY = event.clientY
        this.pullDistance = Math.max(0, this.currentY - this.startY)

        // Use the same robust scroll detection as canPull()
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0
        const isAtTop = scrollTop <= 0

        if (this.pullDistance > 10 && isAtTop) {
            event.preventDefault()
            this.isPulling = true
            this.updateIndicator()
        }
    }

    handleMouseUp(event) {
        if (!this.isPulling || this.isRefreshing) return

        if (this.pullDistance >= this.thresholdValue) {
            this.triggerRefresh()
        } else {
            this.resetIndicator()
        }

        this.isPulling = false
        this.pullDistance = 0
    }

    canPull() {
        // More robust scroll position detection for iOS
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0
        const isAtTop = scrollTop <= 0

        return this.enabledValue && isAtTop && !this.isRefreshing
    }

    updateIndicator() {
        if (!this.hasIndicatorTarget) return

        const progress = Math.min(this.pullDistance / this.thresholdValue, 1)
        const translateY = Math.min(this.pullDistance * 0.5, this.thresholdValue * 0.5)

        this.indicatorTarget.style.transform = `translateY(${translateY}px)`
        this.indicatorTarget.style.opacity = progress

        if (this.hasIconTarget) {
            this.iconTarget.style.transform = `rotate(${progress * 180}deg)`
        }

        if (this.hasTextTarget) {
            this.textTarget.textContent = progress >= 1 ? 'Release to refresh' : 'Pull to refresh'
        }

        // Add visual feedback to the page
        this.element.style.transform = `translateY(${translateY}px)`
    }

    resetIndicator() {
        if (!this.hasIndicatorTarget) return

        // Restore body overflow to allow normal scrolling
        document.body.style.overflow = ''

        this.indicatorTarget.style.transform = 'translateY(-100%)'
        this.indicatorTarget.style.opacity = '0'
        this.element.style.transform = 'translateY(0)'

        if (this.hasIconTarget) {
            this.iconTarget.style.transform = 'rotate(0deg)'
        }

        if (this.hasTextTarget) {
            this.textTarget.textContent = 'Pull to refresh'
        }
    }

    async triggerRefresh() {
        if (this.isRefreshing) return

        this.isRefreshing = true

        if (this.hasIndicatorTarget) {
            this.indicatorTarget.style.transform = 'translateY(0)'
            this.indicatorTarget.style.opacity = '1'
        }

        if (this.hasTextTarget) {
            this.textTarget.textContent = 'Refreshing...'
        }

        if (this.hasIconTarget) {
            this.iconTarget.classList.add('animate-spin')
        }

        try {
            // Use Turbo to refresh the current page
            await visit(window.location.href, {action: "replace"})
        } catch (error) {
            // Fallback to regular page reload
            window.location.reload()
        } finally {
            // Reset after a short delay to show the refresh animation
            setTimeout(() => {
                this.isRefreshing = false
                this.resetIndicator()

                if (this.hasIconTarget) {
                    this.iconTarget.classList.remove('animate-spin')
                }
            }, 500)
        }
    }

    isMobileDevice() {
        return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
            ('ontouchstart' in window) ||
            (navigator.maxTouchPoints > 0)
    }

    isPWA() {
        // Check for iOS Safari PWA mode
        const isIOSStandalone = window.navigator.standalone === true

        // Check for general PWA display mode
        const isStandaloneDisplayMode = window.matchMedia('(display-mode: standalone)').matches

        // Check for Android PWA
        const isAndroidPWA = document.referrer.includes('android-app://')

        // Check for minimal-ui or fullscreen modes (also PWA indicators)
        const isMinimalUI = window.matchMedia('(display-mode: minimal-ui)').matches
        const isFullscreen = window.matchMedia('(display-mode: fullscreen)').matches

        // Additional iOS PWA detection - check if running in Safari but with PWA characteristics
        const isIOSPWA = /iPad|iPhone|iPod/.test(navigator.userAgent) &&
            !window.MSStream &&
            (isIOSStandalone ||
                window.screen.height === window.innerHeight || // Fullscreen-like behavior
                !window.safari) // Safari object not available in PWA mode

        const result = isIOSStandalone || isStandaloneDisplayMode || isAndroidPWA ||
            isMinimalUI || isFullscreen || isIOSPWA

        return result
    }
}
